import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Brutalist social sign-in buttons.
 *
 * Google → Emergent-managed OAuth (works now, no credentials needed)
 * Facebook / Apple / TikTok / Discord → wired UI, marked "soon" until creds are added.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const SocialSignIn = ({ nextPath = "/profile" }) => {
    const navigate = useNavigate();

    const onGoogle = () => {
        // Use the browser's current origin so this works in preview AND production
        // without any env-variable juggling.
        const redirectUrl = window.location.origin + "/auth/callback";
        const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        // Remember where to land after sign-in
        try { sessionStorage.setItem("fitsme.post_auth_next", nextPath); } catch (e) { /* private mode */ }
        window.location.href = url;
    };

    const soon = (provider) => () => {
        // Surfaced via toast in Login.jsx via window event so we don't have to thread a toast hook in
        window.dispatchEvent(new CustomEvent("fitsme:social-soon", { detail: { provider } }));
    };

    const Btn = ({ children, onClick, testId, disabled, iconColor, label }) => (
        <button
            type="button"
            onClick={onClick}
            aria-disabled={disabled || undefined}
            data-testid={testId}
            className={`relative w-full flex items-center justify-center gap-3 border-2 border-black px-4 py-2.5 font-mono text-xs uppercase tracking-widest shadow-brutal-sm ${
                disabled
                    ? "bg-[#FDFBF7] text-black/50 cursor-not-allowed hover:bg-[#FDFBF7]"
                    : "bg-white text-black"
            }`}
            aria-label={label}
        >
            <span className={`w-5 h-5 inline-flex items-center justify-center ${iconColor}`}>{children}</span>
            <span className="flex-1 text-left">{label}</span>
            {disabled && (
                <span className="bg-[#FF00FF] text-white text-[9px] tracking-widest px-1.5 py-0.5 border border-black">
                    SOON
                </span>
            )}
        </button>
    );

    return (
        <div className="space-y-2" data-testid="social-signin-block">
            {/* Google — works now */}
            <Btn
                onClick={onGoogle}
                testId="social-google"
                iconColor="text-black"
                label="Continue with Google"
            >
                {/* Google G icon (SVG) */}
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="#EA4335" d="M12 10.2v3.92h5.45c-.24 1.26-1.62 3.7-5.45 3.7-3.28 0-5.95-2.7-5.95-6.02s2.67-6.02 5.95-6.02c1.86 0 3.11.79 3.83 1.47l2.61-2.52C16.65 3.16 14.55 2.25 12 2.25 6.76 2.25 2.5 6.5 2.5 12s4.26 9.75 9.5 9.75c5.49 0 9.12-3.86 9.12-9.29 0-.62-.07-1.1-.16-1.58H12z"/>
                </svg>
            </Btn>

            {/* Facebook — needs creds */}
            <Btn
                onClick={soon("Facebook")}
                disabled
                testId="social-facebook"
                iconColor="text-[#1877F2]"
                label="Continue with Facebook"
            >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.5-3.9 3.78-3.9 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.77l-.44 2.89h-2.33v6.99A10 10 0 0 0 22 12z"/>
                </svg>
            </Btn>

            {/* Apple */}
            <Btn
                onClick={soon("Apple")}
                disabled
                testId="social-apple"
                iconColor="text-black"
                label="Continue with Apple"
            >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M16.36 12.86c-.02-2.27 1.85-3.36 1.94-3.42-1.06-1.55-2.7-1.76-3.28-1.78-1.4-.14-2.73.82-3.44.82-.71 0-1.81-.8-2.97-.78-1.53.02-2.94.89-3.73 2.25-1.59 2.76-.41 6.85 1.15 9.09.76 1.1 1.66 2.34 2.84 2.3 1.14-.05 1.57-.74 2.95-.74 1.38 0 1.77.74 2.97.71 1.23-.02 2.01-1.12 2.76-2.22.87-1.28 1.23-2.52 1.25-2.59-.03-.01-2.4-.92-2.44-3.64zM14.12 5.97c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.67-1.1 1.76-.96 2.8 1.02.08 2.06-.52 2.69-1.27z"/>
                </svg>
            </Btn>

            {/* TikTok */}
            <Btn
                onClick={soon("TikTok")}
                disabled
                testId="social-tiktok"
                iconColor="text-black"
                label="Continue with TikTok"
            >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M19.5 8.7c-1.74 0-3.36-.55-4.68-1.5v7.2a5.7 5.7 0 1 1-5.7-5.7c.3 0 .6.03.9.07v2.96a2.74 2.74 0 1 0 1.92 2.61V3h2.94c0 .3.03.6.08.9a4.66 4.66 0 0 0 4.54 3.81v.99z"/>
                </svg>
            </Btn>

            {/* X (formerly Twitter) */}
            <Btn
                onClick={soon("X")}
                disabled
                testId="social-x"
                iconColor="text-black"
                label="Continue with X"
            >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z"/>
                </svg>
            </Btn>

            {/* Discord */}
            <Btn
                onClick={soon("Discord")}
                disabled
                testId="social-discord"
                iconColor="text-[#5865F2]"
                label="Continue with Discord"
            >
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                    <path fill="currentColor" d="M20.32 4.65A19.8 19.8 0 0 0 15.5 3.2l-.24.42a18.3 18.3 0 0 0-1.5 2.65 18.5 18.5 0 0 0-3.52 0 18.3 18.3 0 0 0-1.5-2.65L8.5 3.2c-1.7.3-3.32.78-4.82 1.45A20.66 20.66 0 0 0 1 18.86a19.9 19.9 0 0 0 6.04 3.04l.69-.96a13.4 13.4 0 0 1-2.13-1.03l.43-.34a14.2 14.2 0 0 0 7.97 2.07c2.5 0 5.1-.7 7.97-2.07l.43.34a13.4 13.4 0 0 1-2.13 1.03l.69.96A19.9 19.9 0 0 0 23 18.86a20.66 20.66 0 0 0-2.68-14.21zM8.5 15.65c-1.18 0-2.14-1.08-2.14-2.4 0-1.33.94-2.4 2.14-2.4 1.2 0 2.15 1.08 2.14 2.4 0 1.32-.95 2.4-2.14 2.4zm7 0c-1.18 0-2.14-1.08-2.14-2.4 0-1.33.94-2.4 2.14-2.4 1.2 0 2.15 1.08 2.14 2.4 0 1.32-.94 2.4-2.14 2.4z"/>
                </svg>
            </Btn>
        </div>
    );
};
